import { useState } from "react";
import {
  GraduationCap, MapPin, Globe, LogIn, BookOpen, Heart, Shield,
  Users, Award, HandHeart, ArrowRight, Mail, Phone, Clock,
} from "lucide-react";
import { getLang, setLang as setLangStore, t } from "@/lib/i18n";
import AuthPage from "@/components/AuthPage";

export default function HomePage() {
  const [showAuth, setShowAuth] = useState(false);
  const [lang, setLangState] = useState(getLang());

  const changeLanguage = (l: string) => {
    setLangStore(l as any);
    setLangState(l as any);
  };

  if (showAuth) {
    return <AuthPage onBack={() => setShowAuth(false)} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#1C3988] to-[#122554] text-white px-5 py-5 flex items-center justify-between flex-wrap gap-4 shadow-lg sticky top-0 z-50">
        <div className="flex items-center gap-5">
          <div className="w-[75px] h-[75px] bg-white rounded-full flex items-center justify-center border-[3px] border-[#38B6FF] shadow-md flex-shrink-0">
            <GraduationCap className="w-10 h-10 text-[#1C3988]" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-wider">
              NATION <span className="text-[#38B6FF]">UBUNTU</span> SCHOOL
            </h1>
            <p className="text-sm text-[#E2E8F0] mt-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {t("brand_loc")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg border border-[#38B6FF]/30">
            <Globe className="w-4 h-4 text-[#38B6FF]" />
            <select
              value={lang}
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-[#7A2222] text-white border border-[#38B6FF] px-2 py-1 rounded-md font-semibold cursor-pointer outline-none text-sm"
            >
              <option value="en">EN</option>
              <option value="fr">FR</option>
              <option value="pt">PT</option>
            </select>
          </div>
          <button
            onClick={() => setShowAuth(true)}
            className="flex items-center gap-2 bg-[#38B6FF] hover:bg-[#1a9ce0] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all cursor-pointer shadow-md"
          >
            <LogIn className="w-4 h-4" /> {t("hp_login")}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1C3988] via-[#122554] to-[#0d1b3d] text-white py-20 px-5 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#38B6FF] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#7A2222] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-[1100px] mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full border border-[#38B6FF]/30 mb-6">
            <Heart className="w-4 h-4 text-[#38B6FF]" />
            <span className="text-sm font-semibold text-[#38B6FF]">{t("hp_hero_badge")}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-5 leading-tight">
            {t("hp_hero_title")}
          </h2>
          <p className="text-lg text-[#CBD5E1] max-w-2xl mx-auto mb-8 leading-relaxed">
            {t("hp_hero_desc")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => setShowAuth(true)}
              className="flex items-center gap-2 bg-[#38B6FF] hover:bg-[#1a9ce0] text-white px-7 py-3.5 rounded-lg font-semibold transition-all cursor-pointer shadow-lg hover:shadow-xl hover:scale-105"
            >
              <LogIn className="w-5 h-5" /> {t("hp_portal_login")} <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="#about"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-7 py-3.5 rounded-lg font-semibold transition-all cursor-pointer border border-white/20"
            >
              {t("hp_learn_more")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#7A2222] text-white py-8 px-5">
        <div className="max-w-[1100px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-1">
            <Users className="w-8 h-8 text-[#38B6FF] mb-1" />
            <p className="text-3xl font-extrabold">500+</p>
            <p className="text-sm text-[#CBD5E1]">{t("hp_stat_students")}</p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <BookOpen className="w-8 h-8 text-[#38B6FF] mb-1" />
            <p className="text-3xl font-extrabold">3</p>
            <p className="text-sm text-[#CBD5E1]">{t("hp_stat_levels")}</p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <HandHeart className="w-8 h-8 text-[#38B6FF] mb-1" />
            <p className="text-3xl font-extrabold">50+</p>
            <p className="text-sm text-[#CBD5E1]">{t("hp_stat_volunteers")}</p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Award className="w-8 h-8 text-[#38B6FF] mb-1" />
            <p className="text-3xl font-extrabold">15+</p>
            <p className="text-sm text-[#CBD5E1]">{t("hp_stat_years")}</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-5">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-extrabold text-[#1C3988] mb-3">{t("hp_about_title")}</h3>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {t("hp_about_desc")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-7 shadow-[0_10px_25px_rgba(28,57,136,0.08)] border-t-4 border-[#38B6FF] hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-14 h-14 bg-[#38B6FF]/10 rounded-xl flex items-center justify-center mb-4">
                <BookOpen className="w-7 h-7 text-[#38B6FF]" />
              </div>
              <h4 className="text-xl font-bold text-[#1C3988] mb-2">{t("hp_preschool_title")}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t("hp_preschool_desc")}
              </p>
            </div>
            <div className="bg-white rounded-xl p-7 shadow-[0_10px_25px_rgba(28,57,136,0.08)] border-t-4 border-[#1C3988] hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-14 h-14 bg-[#1C3988]/10 rounded-xl flex items-center justify-center mb-4">
                <GraduationCap className="w-7 h-7 text-[#1C3988]" />
              </div>
              <h4 className="text-xl font-bold text-[#1C3988] mb-2">{t("hp_primary_title")}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t("hp_primary_desc")}
              </p>
            </div>
            <div className="bg-white rounded-xl p-7 shadow-[0_10px_25px_rgba(28,57,136,0.08)] border-t-4 border-[#7A2222] hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-14 h-14 bg-[#7A2222]/10 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-[#7A2222]" />
              </div>
              <h4 className="text-xl font-bold text-[#1C3988] mb-2">{t("hp_secondary_title")}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t("hp_secondary_desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 px-5 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-extrabold text-[#1C3988] mb-3">{t("hp_mission_title")}</h3>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {t("hp_mission_desc")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#38B6FF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[#38B6FF]" />
              </div>
              <h4 className="text-lg font-bold text-[#1C3988] mb-2">{t("hp_value_compassion")}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t("hp_value_compassion_desc")}
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#1C3988]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#1C3988]" />
              </div>
              <h4 className="text-lg font-bold text-[#1C3988] mb-2">{t("hp_value_safety")}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t("hp_value_safety_desc")}
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#7A2222]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#7A2222]" />
              </div>
              <h4 className="text-lg font-bold text-[#1C3988] mb-2">{t("hp_value_community")}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t("hp_value_community_desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-5 bg-[#F8FAFC]">
        <div className="max-w-[1100px] mx-auto">
          <div className="bg-gradient-to-br from-[#1C3988] to-[#122554] rounded-2xl p-10 text-white text-center shadow-xl">
            <h3 className="text-3xl font-extrabold mb-3">{t("hp_contact_title")}</h3>
            <p className="text-[#CBD5E1] mb-8 max-w-xl mx-auto">
              {t("hp_contact_desc")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="flex flex-col items-center gap-2">
                <MapPin className="w-6 h-6 text-[#38B6FF]" />
                <p className="text-sm text-[#CBD5E1]">{t("hp_contact_loc1")}</p>
                <p className="text-sm text-[#CBD5E1]">{t("hp_contact_loc2")}</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Mail className="w-6 h-6 text-[#38B6FF]" />
                <p className="text-sm text-[#CBD5E1]">{t("hp_contact_email")}</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Clock className="w-6 h-6 text-[#38B6FF]" />
                <p className="text-sm text-[#CBD5E1]">{t("hp_contact_hours1")}</p>
                <p className="text-sm text-[#CBD5E1]">{t("hp_contact_hours2")}</p>
              </div>
            </div>
            <button
              onClick={() => setShowAuth(true)}
              className="mt-8 inline-flex items-center gap-2 bg-[#38B6FF] hover:bg-[#1a9ce0] text-white px-7 py-3 rounded-lg font-semibold transition-all cursor-pointer shadow-lg hover:scale-105"
            >
              <LogIn className="w-5 h-5" /> {t("hp_access_portal")}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C3988] text-white text-center py-6 text-sm border-t-4 border-[#7A2222] mt-auto">
        <p>&copy; 2026 <strong>Nation Ubuntu School</strong> | Dzaleka - Dowa, P.O. Box 16 Malawi.</p>
        <p><span className="text-[#38B6FF] font-semibold">{t("footer_collab")}</span> Fraternity Without Borders</p>
      </footer>
    </div>
  );
}
